
import React from 'react';
import { X, Users, Calendar, Zap, BarChart2, Settings, ClipboardList, Sparkles } from 'lucide-react';
import { OnboardingSection } from '../../lib/onboardingState';

interface SectionBannerProps {
  section: OnboardingSection;
  onDismiss: () => void;
}

const configs: Record<OnboardingSection, {
  icon: React.ReactNode;
  title: string;
  tip: string;
  color: string;
  border: string;
  iconBg: string;
}> = {
  team: {
    icon: <Users className="w-4 h-4" />,
    title: 'Team Management',
    tip: 'Start by adding your players — include their jersey numbers and positions. Once you have a roster, you can build lineup rotations.',
    color: 'text-violet-700',
    border: 'border-violet-200',
    iconBg: 'bg-violet-100 text-violet-600',
  },
  schedule: {
    icon: <Calendar className="w-4 h-4" />,
    title: 'Schedule',
    tip: 'Add your upcoming games and practices. Tap any game to open it in Game Time for live tracking, or view stats from past games.',
    color: 'text-emerald-700',
    border: 'border-emerald-200',
    iconBg: 'bg-emerald-100 text-emerald-600',
  },
  gametime: {
    icon: <Zap className="w-4 h-4" />,
    title: 'Game Time',
    tip: 'Track scores, rotations, and stats live. Tap player names to log kills, digs, aces, and more.',
    color: 'text-amber-700',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100 text-amber-600',
  },
  stats: {
    icon: <BarChart2 className="w-4 h-4" />,
    title: 'Stats & Analytics',
    tip: 'View hitting efficiency, serve percentage, and reception averages for your team and individual players across all tracked games.',
    color: 'text-rose-700',
    border: 'border-rose-200',
    iconBg: 'bg-rose-100 text-rose-600',
  },
  org: {
    icon: <Settings className="w-4 h-4" />,
    title: 'Organization',
    tip: 'Invite coaches and staff using your join code, manage roles, and configure app settings like substitution limits.',
    color: 'text-slate-700',
    border: 'border-slate-200',
    iconBg: 'bg-slate-100 text-slate-600',
  },
  practice: {
    icon: <ClipboardList className="w-4 h-4" />,
    title: 'Practice Plan',
    tip: 'Build a practice plan by hand — add drills to Warm Up, Skill Development, and Team Play blocks. Or tap "AI Suggestions" to get drills generated from your recent game stats.',
    color: 'text-teal-700',
    border: 'border-teal-200',
    iconBg: 'bg-teal-100 text-teal-600',
  },
};


const SectionBanner: React.FC<SectionBannerProps> = ({ section, onDismiss }) => {
  if (section === 'gametime') return null;

  const cfg = configs[section];

  return (
    <div className={`border ${cfg.border} rounded-xl p-3.5 mb-4 flex items-start gap-3 bg-white`}>
      <div className={`${cfg.iconBg} p-2 rounded-lg shrink-0`}>
        {cfg.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${cfg.color} mb-0.5`}>{cfg.title}</p>
        <p className="text-xs text-slate-500 leading-relaxed">{cfg.tip}</p>
      </div>
      <button
        onClick={onDismiss}
        className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors shrink-0 rounded-lg"
        title="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default SectionBanner;
